import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { API_BASE } from '../../config';
import { useTranslation } from '../../translations';
import { Modal } from '../ui/Modal';
import { Banner } from '../ui/Banner';
import { EmptyState } from '../ui/EmptyState';
import { Badge, TextInput, SecondaryButton } from '../../ui/common';
import { SearchIcon } from '../../ui/icons';
import SimpleSelect from '../../ui/SimpleSelect';
import { formatDateRange, formatMoney } from '../../utils/format';
import {
  candidatesQueryString, groupByMonth, dayStub, pluralCategory, documentBadge, kwotaPoRabacie,
} from '../../utils/reservationPicker';

/**
 * Okno „Nowa faktura → Z rezerwacji".
 *
 * Zadanie okna to znalezc rezerwacje, ktora JESZCZE potrzebuje faktury — stad domyslny zakres
 * „Do zafakturowania" (bez faktury VAT, bez anulowanych). Wyszukiwanie, okres i stronicowanie
 * dzieja sie po stronie serwera (GET /api/invoices/reservation-candidates), bo lista moze miec
 * setki pozycji, a poprzednia wersja ladowala tylko 100 najstarszych.
 *
 * Lista jest ukladana jak dziennik wypozyczen: przyklejone naglowki miesiecy (faktury rozlicza
 * sie miesiecznie) i kartka z dniem przy kazdym wierszu. Gorny pasek kartki niesie stan: kolor
 * akcentu = do zafakturowania, szary = juz zafakturowana albo anulowana.
 */

const SEARCH_DEBOUNCE_MS = 250;

async function fetchCandidates(params) {
  const res = await fetch(
    `${API_BASE}/api/invoices/reservation-candidates?${candidatesQueryString(params)}`,
    { credentials: 'include' }
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ── Pasek filtrow ────────────────────────────────────────────────────────────────────────────

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
`;

const SearchWrap = styled.div`
  position: relative;
  input { padding-left: calc(var(--sp-8) + 2px); }
  svg {
    position: absolute;
    left: var(--sp-3);
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-muted);
    pointer-events: none;
  }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-3);
`;

const Segmented = styled.div`
  display: inline-flex;
  padding: 3px;
  gap: 2px;
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  background: var(--input-bg);

  @media (max-width: 560px) { width: 100%; }
`;

const Segment = styled.button`
  padding: var(--sp-2) var(--sp-3);
  border: none;
  border-radius: calc(var(--r-md) - 4px);
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;
  transition: background-color var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease);

  &:hover { color: var(--text); }
  &:focus-visible { outline: none; box-shadow: var(--focus-ring); }

  @media (max-width: 560px) { flex: 1 1 0; }

  ${(p) => p.$active && css`
    background: var(--info-bg);
    color: var(--info);
    box-shadow: inset 0 0 0 1px var(--info-border);
    &:hover { color: var(--info); }
  `}
`;

const PeriodWrap = styled.div`
  width: 210px;
  @media (max-width: 560px) { width: 100%; }
`;

const Count = styled.span`
  margin-left: auto;
  font-size: var(--fs-sm);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

// ── Lista ────────────────────────────────────────────────────────────────────────────────────

const Groups = styled.div`
  /* Pierwszy naglowek miesiaca ma przylegac do gornej krawedzi przewijanej tresci okna, a nie
     wisiec pod jej paddingiem — wtedy przyklejony naglowek nie przeskakuje przy starcie przewijania. */
  margin-top: calc(-1 * var(--sp-6));
  transition: opacity var(--dur) var(--ease);
  ${(p) => p.$busy && css`opacity: 0.55;`}
`;

const MonthHeader = styled.h3`
  position: sticky;
  /* Sticky w przewijanej Tresci okna liczy sie od krawedzi jej PADDINGU, nie od krawedzi
     widocznego obszaru — przy top: 0 naglowek stal o padding nizej, przykrywal gore pierwszego
     wiersza, a nad nim przeswitywala przewijana lista. */
  top: calc(-1 * var(--sp-6));
  z-index: 1;
  margin: 0 calc(-1 * var(--sp-6));
  padding: var(--sp-3) var(--sp-6) var(--sp-2);
  background: var(--card-bg);
  border-bottom: 1px solid var(--border);
  font-size: var(--fs-xs);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
`;

const RowList = styled.ul`
  list-style: none;
  margin: 0 0 var(--sp-4);
  padding: var(--sp-2) 0 0;

  li + li { border-top: 1px solid var(--border); }
`;

const Row = styled.button`
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  column-gap: var(--sp-4);
  row-gap: var(--sp-2);
  width: 100%;
  margin: 2px 0;
  padding: var(--sp-3);
  border: 1px solid transparent;
  border-radius: var(--r-sm);
  background: transparent;
  color: var(--text);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color var(--dur-fast) var(--ease), border-color var(--dur-fast) var(--ease);

  &:hover { background: var(--hover-bg); }
  &:focus-visible {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  @media (max-width: 560px) {
    grid-template-columns: 44px minmax(0, 1fr);
  }
`;

const DateStub = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 48px;
  border: 1px solid var(--border);
  border-top: 3px solid ${(p) => (p.$settled ? 'var(--border)' : 'var(--primary)')};
  border-radius: var(--r-sm);
  background: var(--panel-bg);
  line-height: 1;
`;

const StubDay = styled.span`
  font-size: var(--fs-lg);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$settled ? 'var(--text-muted)' : 'var(--text)')};
`;

const StubWeekday = styled.span`
  margin-top: 3px;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
`;

const RowMain = styled.span`
  min-width: 0;
`;

const RowTitle = styled.span`
  display: flex;
  align-items: baseline;
  gap: var(--sp-2);
  min-width: 0;
`;

const RowName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 700;
  color: ${(p) => (p.$settled ? 'var(--text-muted)' : 'var(--text)')};
`;

const RowRef = styled.span`
  flex: 0 0 auto;
  font-size: var(--fs-xs);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

const RowMeta = styled.span`
  display: block;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--fs-sm);
  color: var(--text-muted);
`;

const RowSide = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--sp-1);

  @media (max-width: 560px) {
    grid-column: 2;
    min-width: 0;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--sp-2);
  }
`;

const RowAmount = styled.span`
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const Muted = styled.p`
  margin: 0;
  padding: var(--sp-6) 0;
  text-align: center;
  color: var(--text-muted);
`;

const LoadMore = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-3);
  padding-bottom: var(--sp-2);
`;

const BADGE_TONES = { invoice: 'success', proforma: 'info', canceled: 'warning' };

export default function ReservationPicker({ open, onClose, onPick }) {
  const { t, currentLanguage } = useTranslation();

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [scope, setScope] = useState('open');
  const [period, setPeriod] = useState('all');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  // Numer ostatniego zapytania: odpowiedz na starszy filtr (wolniejsza niz nowsza) nie moze
  // nadpisac listy, ktora uzytkownik juz przefiltrowal dalej.
  const requestRef = useRef(0);

  // Zamkniecie czysci filtry, wiec kazde otwarcie zaczyna od „Do zafakturowania" — i tylko jednym
  // zapytaniem (reset przy otwarciu wyslalby najpierw stare filtry, potem domyslne).
  useEffect(() => {
    if (open) return;
    setQ('');
    setDebouncedQ('');
    setScope('open');
    setPeriod('all');
    setRows([]);
    setTotal(0);
    setStatus('loading');
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(q.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q]);

  const load = useCallback(async () => {
    const id = ++requestRef.current;
    setStatus('loading');
    setMoreFailed(false);
    try {
      const data = await fetchCandidates({ q: debouncedQ, scope, period });
      if (id !== requestRef.current) return;
      setRows(data.reservations || []);
      setTotal(data.total || 0);
      setStatus('ready');
    } catch (_) {
      if (id === requestRef.current) setStatus('error');
    }
  }, [debouncedQ, scope, period]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const loadMore = async () => {
    const id = requestRef.current;
    setLoadingMore(true);
    setMoreFailed(false);
    try {
      const data = await fetchCandidates({ q: debouncedQ, scope, period, offset: rows.length });
      if (id !== requestRef.current) return;
      setRows((prev) => [...prev, ...(data.reservations || [])]);
      setTotal(data.total || 0);
    } catch (_) {
      if (id === requestRef.current) setMoreFailed(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const clearFilters = () => {
    setQ('');
    setDebouncedQ('');
    setPeriod('all');
  };

  const groups = useMemo(() => groupByMonth(rows, currentLanguage), [rows, currentLanguage]);
  const filtered = debouncedQ !== '' || period !== 'all';

  const countLabel = (n) => {
    const category = pluralCategory(n, currentLanguage);
    const text = category === 'one'
      ? t('invoices.reservationModal.countOne', { count: n })
      : category === 'few'
        ? t('invoices.reservationModal.countFew', { count: n })
        : t('invoices.reservationModal.countMany', { count: n });
    return text.replace('{count}', n);
  };

  const badgeLabel = (badge) => {
    if (badge.kind === 'invoice') return t('invoices.reservationModal.invoicedBadge').replace('{number}', badge.number);
    if (badge.kind === 'proforma') return t('invoices.reservationModal.proformaBadge').replace('{number}', badge.number);
    return t('invoices.reservationModal.canceledBadge');
  };

  // Enter w wyszukiwarce przy jednym trafieniu od razu je wybiera — najkrotsza droga, gdy
  // uzytkownik zna numer rezerwacji albo nazwisko.
  const onSearchKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (status === 'ready' && rows.length === 1 && debouncedQ === q.trim()) onPick(rows[0].id);
  };

  const periodOptions = [
    { value: 'all', label: t('invoices.reservationModal.periodAll') },
    { value: 'thisMonth', label: t('invoices.reservationModal.periodThisMonth') },
    { value: 'lastMonth', label: t('invoices.reservationModal.periodLastMonth') },
    { value: 'last3Months', label: t('invoices.reservationModal.periodLast3Months') },
  ];

  const toolbar = (
    <Toolbar>
      <SearchWrap>
        <SearchIcon aria-hidden="true" />
        <TextInput
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder={t('invoices.reservationModal.searchPlaceholder')}
          aria-label={t('invoices.reservationModal.searchAria')}
          data-cy="invoice-reservation-search"
        />
      </SearchWrap>
      <Controls>
        <Segmented role="group" aria-label={t('invoices.reservationModal.scopeAria')}>
          <Segment
            type="button"
            $active={scope === 'open'}
            aria-pressed={scope === 'open'}
            onClick={() => setScope('open')}
            data-cy="invoice-reservation-scope-open"
          >
            {t('invoices.reservationModal.scopeOpen')}
          </Segment>
          <Segment
            type="button"
            $active={scope === 'all'}
            aria-pressed={scope === 'all'}
            onClick={() => setScope('all')}
            data-cy="invoice-reservation-scope-all"
          >
            {t('invoices.reservationModal.scopeAll')}
          </Segment>
        </Segmented>
        <PeriodWrap>
          <SimpleSelect
            value={period}
            onChange={setPeriod}
            options={periodOptions}
            style={{ width: '100%' }}
            aria-label={t('invoices.reservationModal.periodAria')}
            data-cy="invoice-reservation-period"
          />
        </PeriodWrap>
        <Count aria-live="polite" data-cy="invoice-reservation-count">
          {status === 'ready' ? countLabel(total) : ''}
        </Count>
      </Controls>
    </Toolbar>
  );

  let body;
  if (status === 'error') {
    body = (
      <Banner tone="danger" compact action={{ label: t('common.retry'), onClick: load }}>
        {t('invoices.reservationModal.loadError')}
      </Banner>
    );
  } else if (status === 'loading' && rows.length === 0) {
    body = <Muted>{t('invoices.reservationModal.loading')}</Muted>;
  } else if (rows.length === 0) {
    const showAll = scope === 'open'
      ? { label: t('invoices.reservationModal.showAll'), onClick: () => setScope('all') }
      : null;
    if (filtered) {
      body = (
        <EmptyState
          compact
          title={t('invoices.reservationModal.noMatchTitle')}
          description={t('invoices.reservationModal.noMatchDescription')}
          action={{ label: t('invoices.reservationModal.clearFilters'), onClick: clearFilters }}
          secondaryAction={showAll}
          data-cy="invoice-reservation-no-match"
        />
      );
    } else if (scope === 'open') {
      body = (
        <EmptyState
          compact
          title={t('invoices.reservationModal.allInvoicedTitle')}
          description={t('invoices.reservationModal.allInvoicedDescription')}
          action={showAll}
          data-cy="invoice-reservation-all-invoiced"
        />
      );
    } else {
      body = <EmptyState compact title={t('invoices.reservationModal.empty')} />;
    }
  } else {
    body = (
      <>
        <Groups $busy={status === 'loading'} aria-busy={status === 'loading'}>
          {groups.map((group) => (
            <section key={group.key}>
              <MonthHeader>{group.label}</MonthHeader>
              <RowList>
                {group.items.map((r) => {
                  const settled = r.invoiced || r.canceled;
                  const stub = dayStub(r.start_date, currentLanguage);
                  const badge = documentBadge(r);
                  const name = r.company_name || r.client_name || t('invoices.reservationModal.noClient');
                  const meta = [
                    formatDateRange(r.start_date, r.end_date, { locale: currentLanguage }),
                    r.company_name ? r.client_name : null,
                    r.equipment_names,
                  ].filter(Boolean).join(' · ');
                  return (
                    <li key={r.id}>
                      <Row type="button" onClick={() => onPick(r.id)} data-cy="invoice-pick-reservation">
                        <DateStub $settled={settled} aria-hidden="true">
                          <StubDay $settled={settled}>{stub.day}</StubDay>
                          <StubWeekday>{stub.weekday}</StubWeekday>
                        </DateStub>
                        <RowMain>
                          <RowTitle>
                            <RowName $settled={settled}>{name}</RowName>
                            <RowRef>#{r.id}</RowRef>
                          </RowTitle>
                          <RowMeta>{meta}</RowMeta>
                        </RowMain>
                        <RowSide>
                          <RowAmount data-cy="invoice-pick-amount">{formatMoney(kwotaPoRabacie(r), { locale: currentLanguage })}</RowAmount>
                          {badge ? <Badge $tone={BADGE_TONES[badge.kind]}>{badgeLabel(badge)}</Badge> : null}
                        </RowSide>
                      </Row>
                    </li>
                  );
                })}
              </RowList>
            </section>
          ))}
        </Groups>
        {rows.length < total ? (
          <LoadMore>
            {moreFailed ? (
              <Banner tone="danger" compact>{t('invoices.reservationModal.loadError')}</Banner>
            ) : null}
            <SecondaryButton type="button" onClick={loadMore} disabled={loadingMore} data-cy="invoice-reservation-load-more">
              {t('invoices.reservationModal.loadMore').replace('{count}', total - rows.length)}
            </SecondaryButton>
          </LoadMore>
        ) : null}
      </>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('invoices.reservationModal.title')}
      toolbar={toolbar}
      size="lg"
      data-cy="invoice-reservation-modal"
    >
      {body}
    </Modal>
  );
}
